import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc,deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useNavigate } from "react-router-dom";

import './ProjectDetails.css';

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState([]);
  

  const [project, setProject] = useState({
    category: [],
    map: {},
    info: {},
    categories: {},
    details: [],
  });

  const fetchProjectDetails = useCallback(async () => {
    const projectRef = doc(db, 'projects', projectId);
    const projectSnapshot = await getDoc(projectRef);
    if (projectSnapshot.exists()) {
      const projectData = { id: projectSnapshot.id, details: [], ...projectSnapshot.data() };
      console.log('Fetched Project:', projectData);
      setProject(projectData);
    } else {
      console.log('No such document!');
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  // Upload file to Firebase Storage
  const uploadFile = async (file, folder = 'images') => {
    setUploading(true);
    try {
      const fileRef = ref(
        storage,
        `projects/${projectId}/${folder}/${Date.now()}-${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      return url;
    } finally {
      setUploading(false);
    }
  };

  // Handle change in simple input fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split('.');
    
    if (nameParts.length === 2) {
      const [outerKey, innerKey] = nameParts;
      setProject((prevProject) => ({
        ...prevProject,
        [outerKey]: {
          ...prevProject[outerKey],
          [innerKey]: value,
        },
      }));
    } else {
      setProject((prevProject) => ({
        ...prevProject,
        [name]: value,
      }));
    }
  };

  // Handle main project image upload
  const handleMainImageUpload = async (file) => {
  if (project.image) setFilesToDelete(prev => [...prev, project.image]);
  const url = await uploadFile(file, 'main');
  setProject(prev => ({ ...prev, image: url }));
  return url;
};


  // Handle change for categories array
  const handleCategoryChange = (index, value) => {
    setProject((prevProject) => {
      const updatedCategories = [...prevProject.category];
      updatedCategories[index] = value;
      return {
        ...prevProject,
        category: updatedCategories,
      };
    });
  };

  // Add new category
  const handleAddCategory = () => {
    setProject((prevProject) => ({
      ...prevProject,
      category: [...prevProject.category, ''],
    }));
  };

  // Remove category
  const handleRemoveCategory = (index) => {
    setProject((prevProject) => ({
      ...prevProject,
      category: prevProject.category.filter((_, i) => i !== index),
    }));
  };

  // Handle subcategory changes
  const handleAddSubcategory = (categoryKey) => {
    setProject((prevProject) => {
      const updatedCategories = {
        ...prevProject.categories,
        [categoryKey]: [...(prevProject.categories[categoryKey] || []), ''],
      };
      return {
        ...prevProject,
        categories: updatedCategories,
      };
    });
  };

  const handleSubcategoryChange = (categoryKey, index, value) => {
    setProject((prevProject) => {
      const updatedSubcategories = [...(prevProject.categories[categoryKey] || [])];
      updatedSubcategories[index] = value;
      return {
        ...prevProject,
        categories: {
          ...prevProject.categories,
          [categoryKey]: updatedSubcategories,
        },
      };
    });
  };

  const handleRemoveSubcategory = (categoryKey, index) => {
    setProject((prevProject) => {
      const updatedSubcategories = prevProject.categories[categoryKey].filter((_, subIndex) => subIndex !== index);
      return {
        ...prevProject,
        categories: {
          ...prevProject.categories,
          [categoryKey]: updatedSubcategories,
        },
      };
    });
  };

  // Add new subcategory key
  const handleAddSubcategoryKey = () => {
    const keyName = prompt('Enter new subcategory key name:');
    if (keyName && !project.categories[keyName]) {
      setProject((prevProject) => ({
        ...prevProject,
        categories: {
          ...prevProject.categories,
          [keyName]: [],
        },
      }));
    }
  };

  // Remove subcategory key
  const handleRemoveSubcategoryKey = (categoryKey) => {
    setProject((prevProject) => {
      const updatedCategories = { ...prevProject.categories };
      delete updatedCategories[categoryKey];
      return {
        ...prevProject,
        categories: updatedCategories,
      };
    });
  };

  // DETAILS ARRAY HANDLERS
  // Handle detail item field change
  const handleDetailChange = (index, field, value) => {
    setProject((prevProject) => {
      const updatedDetails = [...prevProject.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        [field]: value,
      };
      return {
        ...prevProject,
        details: updatedDetails,
      };
    });
  };

  // Handle detail image/video upload
  const handleDetailFileUpload = async (index, field, file, folder = 'images') => {
    if (!file) return;
    const url = await uploadFile(file, folder);
    handleDetailChange(index, field, url);
  };

  // Add new detail item
  const handleAddDetail = () => {
    const typeOptions = ['image', 'section', 'parallel', 'gallery', 'slider', 'slogan', 'double_img', 'video', 'video_only', 'render3d'];
    const selectedType = prompt(`Enter detail type (${typeOptions.join(', ')}):`);
    
    if (selectedType && typeOptions.includes(selectedType)) {
      const newDetail = { type: selectedType, order: project.details.length + 1 };
      
      // Add default fields based on type
      if (selectedType === 'image') {
        newDetail.image = '';
      } else if (selectedType === 'section') {
        newDetail.text = '';
        newDetail.image = '';
        newDetail.align = 'left';
      } else if (selectedType === 'parallel') {
        newDetail.image = '';
        newDetail.caption = '';
      } else if (selectedType === 'gallery' || selectedType === 'slider') {
        newDetail.title = '';
        newDetail.description = '';
        newDetail.images = [];
      } else if (selectedType === 'slogan') {
        newDetail.data = '';
      } else if (selectedType === 'double_img') {
        newDetail.limage = '';
        newDetail.rimage = '';
      } else if (selectedType === 'video' || selectedType === 'video_only') {
        newDetail.video = '';
      } else if (selectedType === 'render3d') {
        newDetail.file = '';
      }
      
      setProject((prevProject) => ({
        ...prevProject,
        details: [...prevProject.details, newDetail],
      }));
    }
  };

  // Remove detail item
  const handleRemoveDetail = (index) => {
    setProject((prevProject) => ({
      ...prevProject,
      details: prevProject.details.filter((_, i) => i !== index),
    }));
  };

  // Move detail up/down
  const handleMoveDetail = (index, direction) => {
    setProject((prevProject) => {
      const updatedDetails = [...prevProject.details];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (newIndex >= 0 && newIndex < updatedDetails.length) {
        [updatedDetails[index], updatedDetails[newIndex]] = [updatedDetails[newIndex], updatedDetails[index]];
      }
      
      return {
        ...prevProject,
        details: updatedDetails,
      };
    });
  };

  // Handle gallery/slider images
  const handleAddGalleryImage = (detailIndex) => {
    setProject((prevProject) => {
      const updatedDetails = [...prevProject.details];
      if (!updatedDetails[detailIndex].images) {
        updatedDetails[detailIndex].images = [];
      }
      updatedDetails[detailIndex].images.push({ image: '', caption: '' });
      
      return {
        ...prevProject,
        details: updatedDetails,
      };
    });
  };

  const handleGalleryImageChange = (detailIndex, imageIndex, field, value) => {
    setProject((prevProject) => {
      const updatedDetails = [...prevProject.details];
      updatedDetails[detailIndex].images[imageIndex][field] = value;
      
      return {
        ...prevProject,
        details: updatedDetails,
      };
    });
  };

  const handleGalleryImageUpload = async (detailIndex, imageIndex, file) => {
    if (!file) return;
    const url = await uploadFile(file, 'images');
    handleGalleryImageChange(detailIndex, imageIndex, 'image', url);
  };

  const handleRemoveGalleryImage = (detailIndex, imageIndex) => {
    setProject((prevProject) => {
      const updatedDetails = [...prevProject.details];
      updatedDetails[detailIndex].images = updatedDetails[detailIndex].images.filter((_, i) => i !== imageIndex);
      
      return {
        ...prevProject,
        details: updatedDetails,
      };
    });
  };
  const handleDeleteProject = async () => {
  const confirmed = window.confirm(
    '⚠️ Are you sure you want to delete this project? This cannot be undone.'
  );

  if (!confirmed) return; // user pressed Cancel

  try {
    // Collect all file URLs to delete from Storage
    const filesToDeleteOnProjectDelete = [];

    // Main project image
    if (project.image) filesToDeleteOnProjectDelete.push(project.image);

    // Details: images, videos, 3D files, gallery/slider images
    project.details.forEach((detail) => {
      if (detail.image) filesToDeleteOnProjectDelete.push(detail.image);
      if (detail.limage) filesToDeleteOnProjectDelete.push(detail.limage);
      if (detail.rimage) filesToDeleteOnProjectDelete.push(detail.rimage);
      if (detail.video) filesToDeleteOnProjectDelete.push(detail.video);
      if (detail.file) filesToDeleteOnProjectDelete.push(detail.file);
      if (detail.images) {
        detail.images.forEach((img) => {
          if (img.image) filesToDeleteOnProjectDelete.push(img.image);
        });
      }
    });

    // Delete all files from Firebase Storage
    for (let fileUrl of filesToDeleteOnProjectDelete) {
      try {
        await deleteObject(ref(storage, fileUrl));
      } catch (err) {
        console.log('Failed to delete file:', fileUrl, err);
      }
    }

    // Delete Firestore document
    await deleteDoc(doc(db, 'projects', projectId));

    alert('Project deleted successfully!');
    // Optional: redirect the user after deletion
     navigate('/projects');
  } catch (err) {
    console.error('Failed to delete project:', err);
    alert('Failed to delete project. Check console for details.');
  }
};


  // Image upload component with preview and drag-drop
  const ImageUploader = ({ value, onUpload, folder = 'images', accept = 'image/*' }) => {
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else if (e.type === 'dragleave') {
        setDragActive(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        onUpload(e.dataTransfer.files[0]);
      }
    };

    const handleFileSelect = (e) => {
      if (e.target.files && e.target.files[0]) {
        onUpload(e.target.files[0]);
      }
    };

  


    return (
      <div style={{ marginBottom: '12px' }}>
        {value && (
          <div style={{ marginBottom: '8px' }}>
            {accept.includes('video') ? (
              <video 
                src={value} 
                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                controls
              />
            ) : accept.includes('image') ? (
              <img 
                src={value} 
                alt="Preview" 
                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }}
              />
            ) : (
              <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                View File
              </a>
            )}
          </div>
        )}
        
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            border: dragActive ? '2px dashed #007bff' : '2px dashed #ccc',
            borderRadius: '4px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: dragActive ? '#f0f8ff' : '#fafafa',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
          onClick={() => document.getElementById(`file-${folder}-${Math.random()}`).click()}
        >
          <input
            id={`file-${folder}-${Math.random()}`}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <p style={{ margin: 0, color: '#666' }}>
            {uploading ? '⏳ Uploading...' : '📁 Click or drag & drop to upload'}
          </p>
        </div>
      </div>
    );
  };

  // Save to Firestore
const handleSave = async () => {
  const projectRef = doc(db, 'projects', projectId);
  const { id, ...projectData } = project;

  // Save project first
  await updateDoc(projectRef, projectData);

  // Now delete old files
  for (let fileUrl of filesToDelete) {
    try {
      await deleteObject(ref(storage, fileUrl));
    } catch (err) {
      console.log('Failed to delete old file:', err);
    }
  }

  setFilesToDelete([]); // reset tracker
  setIsEditing(false);
  alert('Project saved successfully!');
};

  if (!project) return <div>Loading...</div>;

  return (
    <div className="container">
      <h2>Project Details</h2>
      <h3>ID: {project.id}</h3>
      
      {isEditing ? (
        <>
          {/* Basic Fields */}
          <div className="section">
            <h4>Basic Information</h4>
            
            <label>Main Project Image</label>
            <ImageUploader 
              value={project.image}
              onUpload={handleMainImageUpload}
              folder="main"
            />
            
            <input
              type="text"
              name="title"
              value={project.title || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Title"
            />
            <input
              type="number"
              name="order"
              value={project.order || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Order"
            />
            <input
              type="text"
              name="caption"
              value={project.caption || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Caption"
            />
            <textarea
              name="description"
              value={project.description || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Description"
              rows="4"
            />
          </div>

          {/* Map Section */}
          <div className="section">
            <h4>Map Coordinates</h4>
            <input
              type="number"
              name="map.lat"
              value={project.map?.lat || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Latitude"
              step="any"
            />
            <input
              type="number"
              name="map.long"
              value={project.map?.long || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Longitude"
              step="any"
            />
          </div>

          {/* Info Section */}
          <div className="section">
            <h4>Project Info</h4>
            <input
              type="text"
              name="info.client"
              value={project.info?.client || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Client"
            />
            <input
              type="text"
              name="info.scale"
              value={project.info?.scale || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Scale"
            />
            <input
              type="text"
              name="info.year"
              value={project.info?.year || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Year"
            />
            <input
              type="text"
              name="info.status"
              value={project.info?.status || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Status"
            />
            <input
              type="text"
              name="info.location"
              value={project.info?.location || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Location"
            />
            <textarea
              name="info.program"
              value={project.info?.program || ''}
              className="edit-input"
              onChange={handleChange}
              placeholder="Program"
              rows="2"
            />
          </div>

          {/* Categories */}
          <div className="section">
            <h4>Categories</h4>
            {project.category?.map((cat, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <input
                  type="text"
                  value={cat}
                  className="edit-input"
                  onChange={(e) => handleCategoryChange(index, e.target.value)}
                  placeholder={`Category ${index + 1}`}
                />
                <button 
                  className="remove-button" 
                  onClick={() => handleRemoveCategory(index)}
                  title="Remove Category"
                >
                  X
                </button>
              </div>
            ))}
            <button className="add-button" onClick={handleAddCategory}>
              + Add Category
            </button>
          </div>

          {/* Subcategories */}
          <div className="section">
            <h4>Subcategories</h4>
            {Object.keys(project.categories || {}).map((key, index) => (
              <div key={index} style={{ marginBottom: '16px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h5 style={{ margin: 0 }}>{key}</h5>
                  <button 
                    className="remove-button" 
                    onClick={() => handleRemoveSubcategoryKey(key)}
                    title="Remove Subcategory Key"
                  >
                    X
                  </button>
                </div>
                {project.categories[key]?.map((subcat, subIndex) => (
                  <div key={subIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <input
                      type="text"
                      value={subcat}
                      className="edit-input"
                      onChange={(e) => handleSubcategoryChange(key, subIndex, e.target.value)}
                      placeholder={`Subcategory ${subIndex + 1}`}
                    />
                    <button 
                      className="remove-button" 
                      onClick={() => handleRemoveSubcategory(key, subIndex)}
                      title="Remove Subcategory"
                    >
                      X
                    </button>
                  </div>
                ))}
                <button 
                  className="add-button" 
                  onClick={() => handleAddSubcategory(key)}
                  title="Add Subcategory"
                >
                  +
                </button>
              </div>
            ))}
            <button className="add-button" onClick={handleAddSubcategoryKey}>
              + Add Subcategory Key
            </button>
          </div>

          {/* Details Array */}
          <div className="section">
            <h4>Details</h4>
            {project.details?.map((detail, index) => (
              <div key={index} style={{ marginBottom: '20px', padding: '16px', border: '2px solid #007bff', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h5 style={{ margin: 0 }}>Detail {index + 1} - Type: {detail.type}</h5>
                  <div>
                    <button 
                      className="button" 
                      onClick={() => handleMoveDetail(index, 'up')}
                      disabled={index === 0}
                      style={{ marginRight: '4px', padding: '4px 8px' }}
                    >
                      ↑
                    </button>
                    <button 
                      className="button" 
                      onClick={() => handleMoveDetail(index, 'down')}
                      disabled={index === project.details.length - 1}
                      style={{ marginRight: '4px', padding: '4px 8px' }}
                    >
                      ↓
                    </button>
                    <button 
                      className="remove-button" 
                      onClick={() => handleRemoveDetail(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* IMAGE TYPE */}
                {detail.type === 'image' && (
                  <>
                    <label>Image</label>
                    <ImageUploader 
                      value={detail.image}
                      onUpload={(file) => handleDetailFileUpload(index, 'image', file, 'images')}
                    />
                  </>
                )}

                {/* SECTION TYPE */}
                {detail.type === 'section' && (
                  <>
                    <label>Image</label>
                    <ImageUploader 
                      value={detail.image}
                      onUpload={(file) => handleDetailFileUpload(index, 'image', file, 'images')}
                    />
                    <textarea
                      value={detail.text || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'text', e.target.value)}
                      placeholder="Text"
                      rows="3"
                    />
                    <select
                      value={detail.align || 'left'}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'align', e.target.value)}
                    >
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                    <input
                      type="number"
                      value={detail.width || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'width', e.target.value)}
                      placeholder="Width %"
                    />
                  </>
                )}

                {/* PARALLEL TYPE */}
                {detail.type === 'parallel' && (
                  <>
                    <label>Image</label>
                    <ImageUploader 
                      value={detail.image}
                      onUpload={(file) => handleDetailFileUpload(index, 'image', file, 'images')}
                    />
                    <input
                      type="text"
                      value={detail.caption || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'caption', e.target.value)}
                      placeholder="Caption"
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={detail.style2 || false}
                        onChange={(e) => handleDetailChange(index, 'style2', e.target.checked)}
                      />
                      Style 2 (Vertical Layout)
                    </label>
                  </>
                )}

                {/* GALLERY TYPE */}
                {detail.type === 'gallery' && (
                  <>
                    <input
                      type="text"
                      value={detail.title || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'title', e.target.value)}
                      placeholder="Gallery Title"
                    />
                    <textarea
                      value={detail.description || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'description', e.target.value)}
                      placeholder="Gallery Description"
                      rows="3"
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={detail.extend || false}
                        onChange={(e) => handleDetailChange(index, 'extend', e.target.checked)}
                      />
                      4 Images Per Row
                    </label>
                    <h6>Gallery Images</h6>
                    {detail.images?.map((img, imgIndex) => (
                      <div key={imgIndex} style={{ marginLeft: '20px', marginBottom: '12px', padding: '12px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <ImageUploader 
                          value={img.image}
                          onUpload={(file) => handleGalleryImageUpload(index, imgIndex, file)}
                        />
                        <input
                          type="text"
                          value={img.caption || ''}
                          className="edit-input"
                          onChange={(e) => handleGalleryImageChange(index, imgIndex, 'caption', e.target.value)}
                          placeholder="Caption"
                        />
                        <button 
                          className="remove-button" 
                          onClick={() => handleRemoveGalleryImage(index, imgIndex)}
                        >
                          Remove Image
                        </button>
                      </div>
                    ))}
                    <button 
                      className="add-button" 
                      onClick={() => handleAddGalleryImage(index)}
                    >
                      + Add Gallery Image
                    </button>
                  </>
                )}

                {/* SLIDER TYPE */}
                {detail.type === 'slider' && (
                  <>
                    <input
                      type="text"
                      value={detail.header || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'header', e.target.value)}
                      placeholder="Slider Header"
                    />
                    <h6>Slider Images</h6>
                    {detail.images?.map((img, imgIndex) => (
                      <div key={imgIndex} style={{ marginLeft: '20px', marginBottom: '12px', padding: '12px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <ImageUploader 
                          value={img.image}
                          onUpload={(file) => handleGalleryImageUpload(index, imgIndex, file)}
                        />
                        <input
                          type="text"
                          value={img.caption || ''}
                          className="edit-input"
                          onChange={(e) => handleGalleryImageChange(index, imgIndex, 'caption', e.target.value)}
                          placeholder="Caption"
                        />
                        <button 
                          className="remove-button" 
                          onClick={() => handleRemoveGalleryImage(index, imgIndex)}
                        >
                          Remove Slide
                        </button>
                      </div>
                    ))}
                    <button 
                      className="add-button" 
                      onClick={() => handleAddGalleryImage(index)}
                    >
                      + Add Slide
                    </button>
                  </>
                )}

                {/* SLOGAN TYPE */}
                {detail.type === 'slogan' && (
                  <input
                    type="text"
                    value={detail.data || ''}
                    className="edit-input"
                    onChange={(e) => handleDetailChange(index, 'data', e.target.value)}
                    placeholder="Slogan Text"
                  />
                )}

                {/* DOUBLE_IMG TYPE */}
                {detail.type === 'double_img' && (
                  <>
                    <label>Left Image</label>
                    <ImageUploader 
                      value={detail.limage}
                      onUpload={(file) => handleDetailFileUpload(index, 'limage', file, 'images')}
                    />
                    <label>Right Image</label>
                    <ImageUploader 
                      value={detail.rimage}
                      onUpload={(file) => handleDetailFileUpload(index, 'rimage', file, 'images')}
                    />
                  </>
                )}

                {/* VIDEO TYPE */}
                {detail.type === 'video' && (
                  <>
                    <label>Video</label>
                    <ImageUploader 
                      value={detail.video}
                      onUpload={(file) => handleDetailFileUpload(index, 'video', file, 'videos')}
                      accept="video/*"
                    />
                    <input
                      type="text"
                      value={detail.caption || ''}
                      className="edit-input"
                      onChange={(e) => handleDetailChange(index, 'caption', e.target.value)}
                      placeholder="Caption"
                    />
                  </>
                )}

                {/* VIDEO_ONLY TYPE */}
                {detail.type === 'video_only' && (
                  <>
                    <label>Video</label>
                    <ImageUploader 
                      value={detail.video}
                      onUpload={(file) => handleDetailFileUpload(index, 'video', file, 'videos')}
                      accept="video/*"
                    />
                  </>
                )}

                {/* RENDER3D TYPE */}
                {detail.type === 'render3d' && (
                  <>
                    <label>3D Model File</label>
                    <ImageUploader 
                      value={detail.file}
                      onUpload={(file) => handleDetailFileUpload(index, 'file', file, '3d')}
                                           accept=".glb,.gltf,.obj,.fbx"
                    />
                  </>
                )}
              </div>
            ))}
            <button className="add-button" onClick={handleAddDetail}>
              + Add Detail
            </button>
          </div>

          {/* Save / Cancel Buttons */}
          <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
            <button className="save-button" onClick={handleSave}>
              Save Project
            </button>
            <button
              className="cancel-button"
              onClick={() => {
                setIsEditing(false);
                fetchProjectDetails(); // Reset edits
              }}
            >
              Cancel
            </button>
            <button
              className="delete-button"
              onClick={handleDeleteProject}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '12px'
              }}
            >
              Delete Project
            </button>
            
          </div>
        </>
      ) : (
        <>
          {/* Display mode */}
          <div className="section">
            <img
              src={project.image}
              alt={project.title}
              style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '12px' }}
            />
            <h3>{project.title}</h3>
            <p>{project.caption}</p>
            <p>{project.description}</p>
          </div>

          <button
            className="edit-button"
            onClick={() => setIsEditing(true)}
            style={{ marginTop: '20px' }}
          >
            Edit Project
          </button>
        </>
      )}
    </div>
  );
};

export default ProjectDetails;
